'use client';

import type { Country } from '@eliadmin/core/src/api/general/types/general-types';
import { AccountCard } from '@eliadmin/core/src/components/dlz/account/account-card';
import { PROFILE_COPY } from '@eliadmin/core/src/components/dlz/account/profile/profile-copy';
import {
  CheckboxField,
  CountryField,
  TextField,
} from '@eliadmin/core/src/components/dlz/account/profile/profile-fields';
import { REGISTER_COPY } from '@eliadmin/core/src/components/dlz/auth/register/register-copy';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

type AddressNames = {
  street: 'address' | 'delivery_address';
  city: 'city' | 'delivery_city';
  postcode: 'postcode' | 'delivery_postcode';
  country: 'country' | 'delivery_country';
};

const BILLING: AddressNames = {
  street: 'address',
  city: 'city',
  postcode: 'postcode',
  country: 'country',
};

const DELIVERY: AddressNames = {
  street: 'delivery_address',
  city: 'delivery_city',
  postcode: 'delivery_postcode',
  country: 'delivery_country',
};

/** Figma rows 60321:19197 – 60321:19223: street and number, city | postcode (halves), country. */
function AddressRows({
  names,
  countries,
  autoComplete,
}: {
  names: AddressNames;
  countries: Country[];
  autoComplete: 'billing' | 'shipping';
}) {
  const { t } = useTranslations();

  return (
    <>
      <TextField
        name={names.street}
        label={REGISTER_COPY.address.street}
        required
        autoComplete={`${autoComplete} street-address`}
        placeholder={REGISTER_COPY.address.street}
      />
      <div className="grid items-start gap-4 @[40rem]:grid-cols-2">
        <TextField
          name={names.city}
          label={t('text39')}
          required
          autoComplete={`${autoComplete} address-level2`}
          placeholder={t('text39')}
        />
        <TextField
          name={names.postcode}
          label={REGISTER_COPY.address.postcode}
          required
          autoComplete={`${autoComplete} postal-code`}
          placeholder={REGISTER_COPY.address.postcodePlaceholder}
        />
      </div>
      <CountryField
        name={names.country}
        label={t('text519')}
        required
        countries={countries}
        autoComplete={`${autoComplete} country`}
      />
    </>
  );
}

/**
 * Figma „Fakturačná adresa“ (60321:19195; mobile 60459:88683): the address and the switch that
 * reveals the delivery address card.
 */
export function BillingCard({ countries }: { countries: Country[] }) {
  const { t } = useTranslations();

  return (
    <AccountCard title={t('text45')}>
      <AddressRows names={BILLING} countries={countries} autoComplete="billing" />
      <CheckboxField name="same_delivery">{PROFILE_COPY.sameDelivery}</CheckboxField>
    </AccountCard>
  );
}

/**
 * „Doručovacia adresa“ while it differs from the billing one – not drawn in the design (the box is
 * ticked there); the same card and rows as the billing address.
 */
export function DeliveryCard({ countries }: { countries: Country[] }) {
  const { t } = useTranslations();

  return (
    <AccountCard title={t('text2139')}>
      <AddressRows names={DELIVERY} countries={countries} autoComplete="shipping" />
    </AccountCard>
  );
}
